import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import GallerySlider from "../components/Slider/GallerySlider";
import { Toaster, toast } from "sonner";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { normalizeBodyPart } from "../utils/bodyPartNormalizer";

export default function PhotoGalleries() {
  const [gallery, setGallery] = useState([]);
  const location = useLocation();
  const { profile } = useAuth(); // ✅ use AuthContext

  // ✅ Get filter from query param (if redirected after upload)
  const queryParams = new URLSearchParams(location.search);
  const filter = queryParams.get("filter");

  // ✅ Fetch images from backend
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        if (!profile?.accessToken) {
          console.warn("⏳ No token found, skipping gallery fetch");
          return;
        }

        // Fetch both images and current profile
        const [imagesRes, profileRes] = await Promise.all([
          fetch(`${API_BASE}accounts/body-part-images/`, {
            headers: { Authorization: `Bearer ${profile.accessToken}` },
          }),
          fetch(`${API_BASE}accounts/profile/me/`, {
            headers: { Authorization: `Bearer ${profile.accessToken}` },
          })
        ]);

        if (!imagesRes.ok) throw new Error("Failed to fetch gallery");
        const data = await imagesRes.json();
        const profileData = profileRes.ok ? await profileRes.json() : {};

        // Get current active profile image ID from backend
        const activeProfileImageId = profileData.active_profile_image_id;
        console.log("🆔 Active profile image ID from backend:", activeProfileImageId);

        // ✅ Map API response → gallery format
        const formatted = data.map((item, index) => ({
          id: item.id,
          title: `Image ${index + 1}`,
          image: item.image?.startsWith("http")
            ? item.image
            : `${API_BASE.replace("/api/", "")}${item.image}`, // fallback for relative paths
          bodyPart: normalizeBodyPart(item.body_part), // Normalize the body part
          created_at: item.created_at, // Store creation date
          selection: false, // Will be set below
          liked: false,
          is_in_contest: item.is_in_contest || false, // Check if image is used in a contest
        }));

        // Group images by category to find first image in each
        const categoryFirstImage = {};
        formatted.forEach(img => {
          if (!categoryFirstImage[img.bodyPart]) {
            categoryFirstImage[img.bodyPart] = img.id; // Store ID of first image in this category
          }
        });

        // Mark first image in each category as auto-selected
        const formattedWithAutoSelect = formatted.map(img => {
          // If image is in a contest, mark it as active (selection = true)
          if (img.is_in_contest) {
            return {
              ...img,
              selection: true, // Mark as selected/active if in contest
            };
          }
          
          // Special handling for Profile Picture category
          if (img.bodyPart === "Profile Picture") {
            // Mark as selected if this image ID matches the active_profile_image_id from backend
            const isCurrentProfilePic = activeProfileImageId && img.id === activeProfileImageId;
            
            console.log('🔍 Checking Profile Picture image:', {
              imageId: img.id,
              activeProfileImageId: activeProfileImageId,
              isMatch: isCurrentProfilePic
            });
            
            return {
              ...img,
              selection: isCurrentProfilePic,
            };
          }
          
          // Regular auto-entry logic for other categories
          return {
            ...img,
            selection: categoryFirstImage[img.bodyPart] === img.id, // True only for first image in category
          };
        });

        // Debug logging (can be removed in production)
        console.log("📸 Images with auto-select:", formattedWithAutoSelect);
        console.log("📋 First images per category:", categoryFirstImage);
        
        // Log which profile picture image is marked as active
        const activeProfilePic = formattedWithAutoSelect.find(
          img => img.bodyPart === "Profile Picture" && img.selection
        );
        if (activeProfilePic) {
          console.log("✅ Active profile picture image ID:", activeProfilePic.id);
        } else {
          console.log("⚠️ No profile picture image marked as active");
        }

        // ✅ If redirected with filter, show only that filter
        if (filter && filter !== "All Photos") {
          setGallery(formattedWithAutoSelect.filter((img) => img.bodyPart === filter));
        } else {
          setGallery(formattedWithAutoSelect);
        }
      } catch (err) {
        console.error("Gallery load error:", err);
        toast.error("Failed to load gallery images.");
      }
    };

    fetchGallery();
  }, [filter, profile?.accessToken]);
  // Note: Removed profile?.profilePicture dependency to prevent refetch when updating from gallery

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-2 sm:p-4 md:p-6 lg:ml-64 w-full max-w-none">
        <Toaster position="top-center" richColors />
        <Topbar pageTitle="My Photo Galleries" />

        <div className="mt-4">
          {/* ✅ Pass API-fetched images */}
          <GallerySlider gallery={gallery} setGallery={setGallery} />
        </div>
      </div>
    </div>
  );
}
